import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailField } from '@/components/shared/detail-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormField } from '@/components/shared/form-field';
import { Listbox } from '@/components/ui/listbox';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { useAuthStore } from '@/stores/auth-store';
import { formatDate } from '@/utils/format';

const PLATFORM_ROLES = ['none', 'platform_manager', 'platform_admin'] as const;
type PlatformRole = (typeof PLATFORM_ROLES)[number];

interface PlatformUserDetail {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: 'active' | 'suspended' | 'banned' | 'deactivated';
  platformRole: PlatformRole;
  emailVerifiedAt: string | null;
  lastSignInAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const UserDetailDialog = ({
  userId,
  open,
  onOpenChange,
}: {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.platformRole === 'platform_admin';

  const [platformRole, setPlatformRole] = useState<PlatformRole>('none');
  const [roleReason, setRoleReason] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [pendingStatus, setPendingStatus] = useState<'active' | 'suspended' | null>(null);
  const [platformAccessOpen, setPlatformAccessOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['platform-user', userId],
    queryFn: () => api.get<PlatformUserDetail>(`/platform/users/${userId}`),
    enabled: open && Boolean(userId),
  });

  const resetForms = (user?: PlatformUserDetail) => {
    setPlatformRole(user?.platformRole ?? 'none');
    setRoleReason('');
    setStatusReason('');
    setPendingStatus(null);
    setPlatformAccessOpen(false);
  };

  useEffect(() => {
    if (!detail.data) return;
    resetForms(detail.data);
  }, [detail.data]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform-users'] });
    void queryClient.invalidateQueries({ queryKey: ['platform-stats'] });
    if (userId) void queryClient.invalidateQueries({ queryKey: ['platform-user', userId] });
  };

  const updateUser = useMutation({
    mutationFn: (body: {
      status?: 'active' | 'suspended';
      platformRole?: PlatformRole;
      reason: string;
    }) => api.patch(`/platform/users/${userId}`, body),
    onSuccess: (_data, variables) => {
      if (variables.status) {
        toast.success(
          variables.status === 'suspended' ? t('users.suspended') : t('users.reactivated'),
        );
        setPendingStatus(null);
        setStatusReason('');
      } else {
        toast.success(t('users.roleSaved'));
        setPlatformAccessOpen(false);
        setRoleReason('');
      }
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const user = detail.data;
  const isSelf = user?.id === currentUser?.id;

  const roleOptions = useMemo(
    () =>
      PLATFORM_ROLES.map((value) => ({
        value,
        label: value === 'none' ? t('users.detail.noPlatformRole') : t(`platformRoles.${value}`),
      })),
    [t],
  );

  const roleDirty = user && platformRole !== user.platformRole;
  const grantsAdmin = platformRole === 'platform_admin' && user?.platformRole !== 'platform_admin';
  const revokesAdmin = user?.platformRole === 'platform_admin' && platformRole !== 'platform_admin';
  const canChangeStatus =
    isAdmin && user && (user.status === 'active' || user.status === 'suspended') && !isSelf;
  const canManagePlatformAccess = isAdmin && user && !isSelf;

  const close = () => {
    onOpenChange(false);
    resetForms(user);
  };

  const closePlatformAccess = () => {
    setPlatformAccessOpen(false);
    setPlatformRole(user?.platformRole ?? 'none');
    setRoleReason('');
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && close()}>
        <DialogContent closeLabel={t('common.cancel')} size="lg">
          <DialogHeader>
            <DialogTitle>
              {user ? `${user.firstName} ${user.lastName}` : t('users.detailTitle')}
            </DialogTitle>
            <DialogDescription>{t('users.detailDescription')}</DialogDescription>
          </DialogHeader>

          <DialogBody contentClassName="space-y-6">
            {detail.isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner className="size-6 text-brand" />
              </div>
            ) : detail.isError || !user ? (
              <p className="text-body-sm text-danger">{tErrors('UNEXPECTED')}</p>
            ) : (
              <>
                <dl className="space-y-3">
                  <DetailField label={t('users.columns.email')}>{user.email}</DetailField>
                  <DetailField label={t('users.columns.status')}>
                    <Badge variant={user.status === 'active' ? 'success' : 'neutral'}>
                      {t(`status.${user.status}`)}
                    </Badge>
                  </DetailField>
                  <DetailField label={t('users.columns.platformRole')}>
                    {user.platformRole === 'none' ? (
                      <span className="text-fg-secondary">{t('users.detail.noPlatformRole')}</span>
                    ) : (
                      <Badge variant={user.platformRole === 'platform_admin' ? 'brand' : 'info'}>
                        {t(`platformRoles.${user.platformRole}`)}
                      </Badge>
                    )}
                  </DetailField>
                  <DetailField label={t('users.detail.emailVerified')}>
                    {user.emailVerifiedAt ? formatDate(user.emailVerifiedAt) : '—'}
                  </DetailField>
                  <DetailField label={t('users.columns.lastSignIn')}>
                    {user.lastSignInAt ? formatDate(user.lastSignInAt) : '—'}
                  </DetailField>
                  <DetailField label={t('users.columns.created')}>
                    {formatDate(user.createdAt)}
                  </DetailField>
                </dl>

                {isAdmin && isSelf ? (
                  <p className="text-body-sm text-fg-secondary">{t('users.detail.selfNotice')}</p>
                ) : null}

                {canChangeStatus ? (
                  <section className="space-y-4 rounded-lg border border-border p-4">
                    <h3 className="text-body font-medium text-fg">{t('users.detail.statusActions')}</h3>
                    {pendingStatus ? (
                      <>
                        <p className="text-body-sm text-fg-secondary">
                          {pendingStatus === 'suspended'
                            ? t('users.suspendDescription', {
                                name: `${user.firstName} ${user.lastName}`,
                              })
                            : t('users.reactivateDescription', {
                                name: `${user.firstName} ${user.lastName}`,
                              })}
                        </p>
                        <FormField label={t('churches.reason')} required>
                          {(field) => (
                            <Textarea
                              {...field}
                              value={statusReason}
                              onChange={(event) => setStatusReason(event.target.value)}
                              placeholder={t('churches.reasonPlaceholder')}
                            />
                          )}
                        </FormField>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setPendingStatus(null)}>
                            {t('common.cancel')}
                          </Button>
                          <Button
                            size="sm"
                            variant={pendingStatus === 'suspended' ? 'destructive' : 'primary'}
                            loading={updateUser.isPending}
                            disabled={statusReason.trim().length < 3}
                            onClick={() =>
                              updateUser.mutate({
                                status: pendingStatus,
                                reason: statusReason.trim(),
                              })
                            }
                          >
                            {pendingStatus === 'suspended'
                              ? t('users.confirmSuspend')
                              : t('users.confirmReactivate')}
                          </Button>
                        </div>
                      </>
                    ) : user.status === 'active' ? (
                      <div className="flex justify-end">
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setPendingStatus('suspended')}
                        >
                          {t('users.suspend')}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => setPendingStatus('active')}>
                          {t('users.reactivate')}
                        </Button>
                      </div>
                    )}
                  </section>
                ) : null}

                {!isAdmin ? (
                  <p className="text-body-sm text-fg-secondary">{t('users.readOnlyNotice')}</p>
                ) : null}
              </>
            )}
          </DialogBody>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {canManagePlatformAccess ? (
              <Button variant="outline" onClick={() => setPlatformAccessOpen(true)}>
                {t('users.managePlatformAccess')}
              </Button>
            ) : (
              <span />
            )}
            <Button variant="outline" onClick={close}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={platformAccessOpen} onOpenChange={(next) => !next && closePlatformAccess()}>
        <DialogContent closeLabel={t('common.cancel')} size="md">
          {user ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('users.platformAccess.title')}</DialogTitle>
                <DialogDescription>{t('users.platformAccess.description')}</DialogDescription>
              </DialogHeader>

              <DialogBody contentClassName="space-y-4">
                <p className="rounded-md border border-warning/30 bg-warning-subtle px-3 py-2 text-body-sm text-fg">
                  {t('users.platformAccess.warning')}
                </p>

                <DetailField label={t('users.platformAccess.currentRole')}>
                  {user.platformRole === 'none' ? (
                    <span className="text-fg-secondary">{t('users.detail.noPlatformRole')}</span>
                  ) : (
                    t(`platformRoles.${user.platformRole}`)
                  )}
                </DetailField>

                <FormField label={t('users.platformAccess.newRole')} required>
                  {(field) => (
                    <Listbox
                      {...field}
                      popoverPortal="dialog"
                      value={platformRole}
                      onChange={(value) => value && setPlatformRole(value)}
                      options={roleOptions}
                    />
                  )}
                </FormField>

                {grantsAdmin ? (
                  <p className="text-body-sm text-danger">{t('users.platformAccess.grantAdminWarning')}</p>
                ) : null}
                {revokesAdmin ? (
                  <p className="text-body-sm text-fg-secondary">
                    {t('users.platformAccess.revokeAdminWarning')}
                  </p>
                ) : null}

                <FormField label={t('churches.reason')} required>
                  {(field) => (
                    <Textarea
                      {...field}
                      value={roleReason}
                      onChange={(event) => setRoleReason(event.target.value)}
                      placeholder={t('users.platformAccess.reasonPlaceholder')}
                    />
                  )}
                </FormField>
              </DialogBody>

              <DialogFooter>
                <Button variant="outline" onClick={closePlatformAccess}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant={grantsAdmin || revokesAdmin ? 'destructive' : 'primary'}
                  loading={updateUser.isPending}
                  disabled={!roleDirty || roleReason.trim().length < 3}
                  onClick={() =>
                    updateUser.mutate({
                      platformRole,
                      reason: roleReason.trim(),
                    })
                  }
                >
                  {t('users.platformAccess.confirm')}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};
